import { AutoComplete, Button, Tag } from 'antd'
import classNames from 'classnames'
import { useMemo, useState } from 'react'
import { useDebounce } from 'react-use'

import { ErrorLabel } from 'common/components/ErrorLabel/ErrorLabel'
import { useLazyFetchEmrCodesQuery } from 'features/Procedure/state/procedure.api'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'
import { SERIES_PAGES_TEXT } from 'features/Series/pages/shared/seriesPagesText'

import type { TemplateIssue } from '../templateForm.model'
import styles from './templateFields.module.scss'

const ISSUES_MAX = 20
const DEBOUNCE_MS = 500
const MIN_SEARCH_LENGTH = 1
const TEXT = SERIES_PAGES_TEXT.modal

type Props = {
  value: TemplateIssue[]
  onChange: (value: TemplateIssue[]) => void
  siteId: string
  disabled?: boolean
  error?: string
}

const refOf = (i: Pick<TemplateIssue, 'type' | 'code'>) => `${i.type}:${i.code}`

// "ICD10:L71.9" splits at the first colon into type and code.
const parseRef = (ref: string): { type: string; code: string } => {
  const at = ref.indexOf(':')
  return at < 0 ? { type: '', code: ref } : { type: ref.slice(0, at), code: ref.slice(at + 1) }
}

// Problem search by ICD code, free of any patient or appointment.
export const TemplateIssuesField = ({
  value,
  onChange,
  siteId,
  disabled = false,
  error,
}: Props) => {
  const [term, setTerm] = useState('')
  const full = value.length >= ISSUES_MAX
  const [fetchCodes, { data }] = useLazyFetchEmrCodesQuery()
  const searchable = !full && !disabled

  useDebounce(
    () => {
      if (!searchable || !siteId || term.length < MIN_SEARCH_LENGTH) return
      fetchCodes({ siteId, search: term, code_type: ProcedureTypeCode.ICD10 })
    },
    DEBOUNCE_MS,
    [term, siteId, searchable],
  )

  const options = useMemo(
    () =>
      (data ?? []).map((row) => {
        const code = row.code.replace(/\s+/g, '')
        const description = row.code_text.replace(/<\/?[^>]+(>|$)/g, '').trim()
        const ref = refOf({ type: row.code_type, code })
        return { value: ref, label: `${description} ${ref}`, description }
      }),
    [data],
  )

  const handleSelect = (ref: string, option: { description?: string }) => {
    const { type, code } = parseRef(ref)
    setTerm('')
    if (value.some((i) => i.type === type && i.code === code)) return
    onChange([...value, { type, code, description: option.description ?? '' }])
  }

  const handleRemove = (issue: TemplateIssue) =>
    onChange(value.filter((i) => refOf(i) !== refOf(issue)))

  return (
    <div className={styles.issueField}>
      <div
        className={classNames(styles.issueBox, {
          [styles.issueBoxError]: Boolean(error),
          [styles.issueBoxDisabled]: disabled,
        })}>
        {value.map((issue) => (
          <Tag
            key={refOf(issue)}
            className={styles.issueChip}
            closable={!disabled}
            closeIcon={<span aria-label={`Remove ${issue.description}`}>×</span>}
            onClose={() => handleRemove(issue)}>
            {issue.description} <span className={styles.issueCode}>{refOf(issue)}</span>
          </Tag>
        ))}
        {!full && (
          <AutoComplete
            className={styles.issueSearch}
            variant='borderless'
            value={term}
            options={options}
            filterOption={false}
            disabled={disabled}
            placeholder={value.length ? TEXT.addAnother : TEXT.issuePlaceholder}
            onSearch={setTerm}
            onSelect={handleSelect}
          />
        )}
      </div>
      {value.length > 0 && !disabled && (
        <Button size='small' className={styles.clear} onClick={() => onChange([])}>
          {TEXT.clear}
        </Button>
      )}
      <ErrorLabel error={error} />
    </div>
  )
}
